import React from 'react';
import { Navigate, Route } from 'react-router';

import { AnimatedRoute, AnimatedRoutes } from '@wildidea/components/AnimatedRoutes/AnimatedRoutes';
import { useBodyClass } from '@wildidea/components/hooks/useBodyClass';
import { isTouchOnlyDevice } from '@wildidea/components/utils/isTouchOnlyDevice';

import { PageViewBeacon } from './PageViewBeacon.js';
import AboutRoute from './routes/about/AboutRoute.js';
import AmbassadorsRoute from './routes/ambassadors/AmbassadorsRoute.js';
import BAARoute from './routes/baa/BAARoute.js';
import HomeRoute from './routes/home/HomeRoute.js';
import NewsRoute from './routes/news/NewsRoute.js';
import PricingRoute from './routes/pricing/PricingRoute.js';
import PrivacyPolicyRoute from './routes/privacy/PrivacyPolicyRoute.js';
import ScribeAgreementRoute from './routes/scribe-agreement/ScribeAgreementRoute.js';
import SecurityRoute from './routes/security/SecurityRoute.js';
import SMSTermsAndPrivacyRoute from './routes/sms/SMSTermsAndPrivacyRoute.js';
import TeamsRoute from './routes/teams/TeamsRoute.js';
import TermsOfServiceRoute, {
  TermsOfServiceFrameRoute
} from './routes/terms/TermsOfServiceRoute.js';
import { RUMStateListener } from './telemetry.js';

import './viewport.css';

export const App: React.FC<{}> = () => {
  useBodyClass(isTouchOnlyDevice() ? 'touch-only' : null);

  return (
    <>
      <header className="header"></header>
      <main>
        <PageViewBeacon />
        <AnimatedRoutes>
          <AnimatedRoute
            path="/strap"
            key="strap"
            element={
              <React.Suspense fallback={null}>
                <StrapRouteRedirect />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/privacy"
            key="privacy"
            element={
              <React.Suspense fallback={null}>
                <PrivacyPolicyRoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/scribe-agreement"
            key="scribe-agreement"
            element={
              <React.Suspense fallback={null}>
                <ScribeAgreementRoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/sms"
            key="sms"
            element={
              <React.Suspense fallback={null}>
                <SMSTermsAndPrivacyRoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/terms"
            key="terms"
            element={
              <React.Suspense fallback={null}>
                <TermsOfServiceRoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/terms-frame"
            key="terms-frame"
            element={
              <React.Suspense fallback={null}>
                <TermsOfServiceFrameRoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/pricing"
            key="pricing"
            element={
              <React.Suspense fallback={null}>
                <PricingRoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/ambassadors"
            key="ambassadors"
            element={
              <React.Suspense fallback={null}>
                <AmbassadorsRoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/security"
            key="security"
            element={
              <React.Suspense fallback={null}>
                <SecurityRoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/baa"
            key="baa"
            element={
              <React.Suspense fallback={null}>
                <BAARoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/about"
            key="about"
            element={
              <React.Suspense fallback={null}>
                <AboutRoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/news"
            key="news"
            element={
              <React.Suspense fallback={null}>
                <NewsRoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/news/2026-09-15-sarcon"
            key="news-2026-09-15-sarcon"
            element={
              <React.Suspense fallback={null}>
                <NewsRoute slug="2026-09-15-sarcon" />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/news/2026-09-03-voice-scribe"
            key="news-2026-09-03-voice-scribe"
            element={
              <React.Suspense fallback={null}>
                <NewsRoute slug="2026-09-03-voice-scribe" />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/teams"
            key="teams"
            element={
              <React.Suspense fallback={null}>
                <TeamsRoute />
              </React.Suspense>
            }
          />

          <AnimatedRoute
            path="/"
            key="home"
            element={
              <React.Suspense fallback={null}>
                <HomeRoute />
              </React.Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </AnimatedRoutes>
      </main>
      <RUMStateListener utmSource={window.sessionStorage.getItem('utm/source') || undefined} />
    </>
  );
};

const StrapRouteRedirect = () => {
  React.useEffect(function redirectFromStrapRoute() {
    window.sessionStorage.setItem('utm/source', 'strap');
    return () => {
      window.location.assign('/demo/scribe');
    };
  }, []);
  return <Navigate to="/" replace />;
};
